import { useContext, useMemo, useEffect } from 'react';
import { Container, Grid, Box } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useTranslation } from 'react-i18next';
import { AppContext, SttPage } from '../../sporttia/all';
import constants from '../../config/constants';
import {
    contactStyles,
    Hero,
    InfoBox,
    MethodCard,
    buildWaLink,
} from '../../components/public/contact/ContactShared';

type ContactMethod = {
    key: string;
    icon: React.ReactElement;
    title: string;
    description: string;
    contact: string;
    href: string;
    badgeClass: string;
    btnClass: string;
    accent: string;
    buttonText: string;
};

export default function Contact(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    // Set page header title
    useEffect(() => {
        cxt.setHeader({ title: t('contact.title') });
    }, []);

    const email = useMemo(
        () => cxt?.constants?.contactEmail || 'info@sporttia.com',
        [cxt?.constants],
    );
    const tel = useMemo(
        () => cxt?.constants?.contactPhone || '+34 900 000 000',
        [cxt?.constants],
    );
    const wa = useMemo(
        () => cxt?.constants?.contactWhatsAppDisplay || '+34 900 000 000',
        [cxt?.constants],
    );

    const allMethods: ContactMethod[] = [
        {
            key: 'email',
            icon: <MailOutlineIcon />,
            title: t('Email'),
            description: t('contact.emailDescription'),
            contact: email,
            href: `mailto:${email}`,
            badgeClass: 'badgeBlue',
            btnClass: 'btnBlue',
            accent: 'linear-gradient(135deg,#3B82F6,#60A5FA)',
            buttonText: t('contact.sendEmail'),
        },
        {
            key: 'whatsapp',
            icon: <WhatsAppIcon />,
            title: 'WhatsApp',
            description: t('contact.whatsappDescription'),
            contact: wa,
            href: buildWaLink(wa, t('contact.whatsappMessage')),
            badgeClass: 'badgeGreen',
            btnClass: 'btnGreen',
            accent: 'linear-gradient(135deg,#22C55E,#16A34A)',
            buttonText: t('contact.openWhatsapp'),
        },
        {
            key: 'phone',
            icon: <PhoneInTalkIcon />,
            title: t('contact.contactPhone'),
            description: t('contact.phoneDescription'),
            contact: tel,
            href: `tel:${tel}`,
            badgeClass: 'badgeWa',
            btnClass: 'btnOrange',
            accent: 'linear-gradient(135deg,#F59E0B,#F97316)',
            buttonText: t('contact.callNow'),
        },
    ];

    // El manual del gestor, publicado como documentación online. Va primero
    // porque es autoservicio: resuelve sin esperar respuesta, y los canales de
    // contacto quedan para lo que el manual no cubre.
    const documentationMethod: ContactMethod = {
        key: 'documentation',
        icon: <MenuBookIcon />,
        title: t('common.documentation'),
        description: t('contact.documentationDescription'),
        contact: new URL(constants.docs.link).host,
        href: constants.docs.link,
        badgeClass: 'badgeSky',
        btnClass: 'btnSky',
        // El degradado del reflejo es el mismo del botón, que ya está definido
        // en contactStyles: así no se repiten los colores aquí.
        accent: contactStyles.btnSky.background,
        buttonText: t('contact.openDocumentation'),
    };

    // In zero mode show only email, otherwise show phone and whatsapp. La
    // documentación se ofrece siempre: no depende del plan del centro.
    const methods = [
        documentationMethod,
        ...(cxt?.zero
            ? allMethods.filter((m) => m.key === 'email')
            : allMethods.filter((m) => ['phone', 'whatsapp'].includes(m.key))),
    ];

    return (
        <Container maxWidth="lg">
            <SttPage>
                <Box sx={contactStyles.pageBg}>
                    <Hero
                        title={t('contact.contactTitle')}
                        subtitle={t('contact.contactSubtitle')}
                    />
                    <Box sx={contactStyles.gridWrap}>
                        <Grid container spacing={3} justifyContent="center">
                            {methods.map((m: any) => (
                                <Grid
                                    key={m.key}
                                    size={{ xs: 12, sm: 6, md: 3 }}
                                >
                                    <MethodCard {...m} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                    <InfoBox
                        icon={<ScheduleIcon />}
                        title={t('contact.scheduleTitle')}
                        text={t('contact.responseTime')}
                    />
                </Box>
            </SttPage>
        </Container>
    );
}
